import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

/** Returns the set of order IDs the current user has starred (subscribed to). */
export function useStarredOrderIds() {
  const { user } = useAuth();
  const { tenantId, tenantReady } = useTenant();

  return useQuery({
    queryKey: ["order-stars", user?.id, tenantId],
    enabled: !!user?.id && tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_stars")
        .select("order_id")
        .eq("user_id", user!.id)
        .eq("tenant_id", tenantId!);
      if (error) throw error;
      return new Set((data ?? []).map((r: { order_id: string }) => r.order_id));
    },
  });
}

export function useToggleOrderStar() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, starred }: { orderId: string; starred: boolean }) => {
      if (!user?.id || !tenantId) throw new Error("Not authenticated");
      if (starred) {
        const { error } = await supabase
          .from("order_stars")
          .delete()
          .eq("user_id", user.id)
          .eq("order_id", orderId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("order_stars")
          .insert({ user_id: user.id, order_id: orderId, tenant_id: tenantId });
        if (error) throw error;
      }
    },
    onMutate: async ({ orderId, starred }) => {
      const key = ["order-stars", user?.id, tenantId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<Set<string>>(key);
      const next = new Set(prev ?? []);
      if (starred) next.delete(orderId);
      else next.add(orderId);
      queryClient.setQueryData(key, next);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(["order-stars", user?.id, tenantId], ctx.prev);
      }
      toast.error("Failed to update subscription");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["order-stars", user?.id, tenantId] });
      queryClient.invalidateQueries({ queryKey: ["starred-orders"] });
    },
  });
}